/**
 * CRO Search — Location-aware search overlay
 *
 * Combines two search sources:
 * 1. Local location index (instant, from cro-location-index.js)
 * 2. Shopify Predictive Search API (async, for product results)
 *
 * Dependencies: cro-location-index.js must be loaded first.
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     DOM references
     ----------------------------------------------------------------------- */
  var overlay   = document.getElementById('CroSearchOverlay');
  var input     = document.getElementById('CroSearchInput');
  var form      = document.getElementById('CroSearchForm');
  var closeBtn  = document.getElementById('CroSearchClose');
  var bodyEl    = document.getElementById('CroSearchBody');
  var defaultEl = document.getElementById('CroSearchDefault');
  var resultsEl = document.getElementById('CroSearchResults');
  var emptyEl   = document.getElementById('CroSearchEmpty');

  if (!overlay || !input || !defaultEl || !resultsEl || !emptyEl) return;

  /* -----------------------------------------------------------------------
     State
     ----------------------------------------------------------------------- */
  var debounceTimer = null;
  var currentQuery  = '';
  var focusedIndex  = -1;
  var allResultLinks = [];
  var activeRequestId = 0; /* Monotonic counter for race condition prevention */
  var hoverTimer = null;
  var hoverRequestId = 0;
  var defaultProductsHtml = '';
  var productsContainer = null;
  var activeLocItem = null;
  var currentLocationResults = [];

  /* -----------------------------------------------------------------------
     Location type icons (emoji for simplicity, could be SVG)
     ----------------------------------------------------------------------- */
  var typeIcons = {
    'state':         '🏛',
    'city':          '🏙',
    'college':       '🎓',
    'national-park': '🏔',
    'region':        '📍',
    'country':       '🌍',
    'ski-resort':    '⛷'
  };

  var typeLabels = (window.CRO && window.CRO.locationTypeLabels) || {};

  /* -----------------------------------------------------------------------
     Open / Close
     ----------------------------------------------------------------------- */
  function openSearch() {
    overlay.classList.add('cro-search--active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(function () { input.focus(); }, 50);
    showDefault();
  }

  function closeSearch() {
    overlay.classList.remove('cro-search--active');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    input.value = '';
    currentQuery = '';
    clearTimeout(debounceTimer);
    activeRequestId++; /* Cancel any pending product fetches */
    focusedIndex = -1;
  }

  /* Set initial ARIA state */
  overlay.setAttribute('aria-hidden', 'true');

  /* Bind open triggers — any element with data-cro-search-open */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-cro-search-open]');
    if (trigger) {
      e.preventDefault();
      openSearch();
    }
  });

  /* Close button */
  if (closeBtn) {
    closeBtn.addEventListener('click', closeSearch);
  }

  /* Click outside modal to close */
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closeSearch();
  });

  /* Escape key to close */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay.classList.contains('cro-search--active')) {
      closeSearch();
    }
    /* Cmd/Ctrl+K to open */
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      if (overlay.classList.contains('cro-search--active')) {
        closeSearch();
      } else {
        openSearch();
      }
    }
  });

  /* -----------------------------------------------------------------------
     Search logic
     ----------------------------------------------------------------------- */
  input.addEventListener('input', function () {
    var q = input.value.trim();

    if (q.length === 0) {
      currentQuery = '';
      showDefault();
      return;
    }

    currentQuery = q;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      performSearch(q);
    }, q.length < 3 ? 200 : 150);
  });

  /* Form submit — go to Shopify search results page */
  if (form) {
    form.addEventListener('submit', function () {
      closeSearch();
    });
  }

  /* -----------------------------------------------------------------------
     Display states
     ----------------------------------------------------------------------- */
  function showDefault() {
    defaultEl.style.display = '';
    resultsEl.style.display = 'none';
    emptyEl.style.display = 'none';
    bodyEl.classList.remove('cro-search-body--has-split');
    focusedIndex = -1;
    allResultLinks = [];
  }

  function showResults(html) {
    defaultEl.style.display = 'none';
    resultsEl.style.display = '';
    emptyEl.style.display = 'none';
    bodyEl.classList.add('cro-search-body--has-split');
    resultsEl.innerHTML = html;
    focusedIndex = -1;
    refreshResultLinks();
  }

  function showEmpty() {
    defaultEl.style.display = 'none';
    resultsEl.style.display = 'none';
    emptyEl.style.display = '';
    bodyEl.classList.remove('cro-search-body--has-split');
    focusedIndex = -1;
    allResultLinks = [];
  }

  /* -----------------------------------------------------------------------
     Core search: location index + Shopify API
     ----------------------------------------------------------------------- */
  function performSearch(query) {
    var locationResults = searchLocations(query);
    var hasLocations = locationResults.length > 0;

    /* Reset hover state */
    defaultProductsHtml = '';
    productsContainer = null;
    activeLocItem = null;
    currentLocationResults = locationResults;
    clearTimeout(hoverTimer);
    hoverRequestId++;

    /* Increment request ID — any callback with an older ID is stale */
    var requestId = ++activeRequestId;

    /* Build location HTML once (reused in temp and final render) */
    var locationHtml = '';
    if (hasLocations) {
      locationHtml += '<div class="cro-search-section cro-search-section--locations">';
      locationHtml += '<h3 class="cro-search-section-title">Places & Colleges</h3>';
      locationHtml += '<ul class="cro-search-results-list" role="listbox">';
      locationResults.forEach(function (loc, idx) {
        locationHtml += renderLocationResult(loc, query, idx);
      });
      locationHtml += '</ul></div>';
    }

    /* Show location results immediately + loading indicator for products */
    var tempHtml = locationHtml;
    tempHtml += '<div class="cro-search-section cro-search-section--products" id="CroSearchProducts">';
    tempHtml += '<p class="cro-search-loading">Searching products…</p>';
    tempHtml += '</div>';

    /* View all link */
    tempHtml += '<div class="cro-search-section cro-search-section--full">';
    tempHtml += '<a href="/search?q=' + encodeURIComponent(query) + '&type=product" class="cro-search-view-all">';
    tempHtml += 'View all results for &ldquo;' + escapeHtml(query) + '&rdquo;';
    tempHtml += '</a></div>';

    showResults(tempHtml);
    productsContainer = document.getElementById('CroSearchProducts');

    /* Bind hover handlers on location items */
    if (hasLocations) {
      bindLocationHover(locationResults);
    }

    /* Fetch product results from Shopify in parallel */
    fetchProducts(query, function (products) {
      /* Discard stale responses */
      if (requestId !== activeRequestId) return;

      var hasProducts = products && products.length > 0;

      if (!hasLocations && !hasProducts) {
        showEmpty();
        return;
      }

      /* Build and store the default products HTML */
      defaultProductsHtml = buildProductsHtml(products);

      /* Only update products if no location is currently hovered */
      if (!activeLocItem && productsContainer) {
        productsContainer.innerHTML = defaultProductsHtml;
        refreshResultLinks();
      }
    });
  }

  function buildProductsHtml(products) {
    if (!products || products.length === 0) return '';
    var html = '<h3 class="cro-search-section-title">Products</h3>';
    html += '<ul class="cro-search-products-list">';
    products.forEach(function (p) {
      html += renderProductResult(p);
    });
    html += '</ul>';
    return html;
  }

  /* -----------------------------------------------------------------------
     Location hover → update products
     ----------------------------------------------------------------------- */
  function bindLocationHover(locationResults) {
    var items = resultsEl.querySelectorAll('.cro-search-result-item');
    var locList = resultsEl.querySelector('.cro-search-results-list');

    items.forEach(function (item, idx) {
      var loc = locationResults[idx];
      if (!loc) return;

      item.addEventListener('mouseenter', function () {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          setActiveLocation(item, loc);
        }, 150);
      });
    });

    /* Mouse leaves the entire locations list → restore defaults */
    if (locList) {
      locList.addEventListener('mouseleave', function () {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          clearActiveLocation();
        }, 100);
      });
    }
  }

  function setActiveLocation(item, loc) {
    /* Highlight the active location */
    if (activeLocItem) activeLocItem.classList.remove('cro-search-result-item--active');
    item.classList.add('cro-search-result-item--active');
    activeLocItem = item;

    if (!productsContainer) return;

    /* Show loading state */
    productsContainer.classList.add('cro-search-section--loading');

    /* Fetch products for this location */
    var rid = ++hoverRequestId;
    fetchProducts(loc.name, function (products) {
      if (rid !== hoverRequestId) return;
      if (!productsContainer) return;
      productsContainer.classList.remove('cro-search-section--loading');

      var html = buildProductsHtml(products);
      if (!html) {
        html = '<p class="cro-search-products-empty">No products for ' + escapeHtml(loc.name) + '</p>';
      }
      productsContainer.innerHTML = html;
      refreshResultLinks();
    });
  }

  function clearActiveLocation() {
    if (activeLocItem) {
      activeLocItem.classList.remove('cro-search-result-item--active');
      activeLocItem = null;
    }
    if (productsContainer && defaultProductsHtml) {
      productsContainer.classList.remove('cro-search-section--loading');
      productsContainer.innerHTML = defaultProductsHtml;
      refreshResultLinks();
    }
  }

  function refreshResultLinks() {
    allResultLinks = Array.prototype.slice.call(
      resultsEl.querySelectorAll('.cro-search-result-link, .cro-search-product-link')
    );
  }

  /* -----------------------------------------------------------------------
     Location index search
     ----------------------------------------------------------------------- */
  function searchLocations(query) {
    if (!window.CRO || !window.CRO.locationIndex) return [];

    var q = query.toLowerCase();
    var results = [];

    window.CRO.locationIndex.forEach(function (loc) {
      var name = loc.name.toLowerCase();
      var slug = loc.slug;
      var score = 0;

      if (name === q) {
        score = 100;
      } else if (name.indexOf(q) === 0) {
        score = 80;
      } else if (slug.indexOf(q) === 0) {
        score = 75;
      } else if (name.indexOf(q) > 0) {
        score = 50;
      } else if (slug.indexOf(q.replace(/\s+/g, '-')) > -1) {
        score = 40;
      }

      /* Also match parent states */
      if (score === 0 && loc.parentStates) {
        for (var i = 0; i < loc.parentStates.length; i++) {
          if (loc.parentStates[i].indexOf(q) === 0) {
            score = 30;
            break;
          }
        }
      }

      if (score > 0) {
        results.push({ loc: loc, score: score });
      }
    });

    /* Sort by score desc, then alphabetically */
    results.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.loc.name.localeCompare(b.loc.name);
    });

    return results.slice(0, 8).map(function (r) { return r.loc; });
  }

  /* -----------------------------------------------------------------------
     Shopify Predictive Search API
     ----------------------------------------------------------------------- */
  function fetchProducts(query, callback) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=4' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,variants.title,tag';

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = [];
        if (data.resources && data.resources.results && data.resources.results.products) {
          products = data.resources.results.products;
        }
        callback(products);
      })
      .catch(function () {
        callback([]);
      });
  }

  /* -----------------------------------------------------------------------
     Rendering
     ----------------------------------------------------------------------- */
  function buildLocationUrl(loc) {
    /*
     * Build a tag-filtered collection URL for this location.
     * Use colon format (loc-name:california) not handleized (loc-name-california)
     * because handleized is ambiguous when values contain hyphens
     * (e.g. loc-name-new-york-state could be loc-name:new-york-state or loc-name-new:york-state).
     */
    var tags = [];
    if (loc.category) tags.push('category:' + loc.category);
    tags.push('loc-name:' + loc.slug);
    return '/collections/all/' + tags.join('+');
  }

  function renderLocationResult(loc, query, idx) {
    var url = buildLocationUrl(loc);
    var icon = typeIcons[loc.type] || '📍';
    var label = typeLabels[loc.type] || loc.type;
    var name = highlightMatch(loc.name, query);
    var meta = escapeHtml(label);

    if (loc.parentStates && loc.parentStates.length > 0 && loc.parentStates.length <= 2) {
      meta += ' &middot; ' + loc.parentStates.map(function (s) {
        var formatted = s.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        return escapeHtml(formatted);
      }).join(', ');
    }

    return '<li class="cro-search-result-item" role="option">' +
      '<a href="' + escapeHtml(url) + '" class="cro-search-result-link">' +
      '<span class="cro-search-result-icon cro-search-result-icon--' + loc.type + '">' + icon + '</span>' +
      '<span class="cro-search-result-text">' +
      '<span class="cro-search-result-name">' + name + '</span>' +
      '<span class="cro-search-result-meta">' + meta + '</span>' +
      '</span>' +
      '<span class="cro-search-result-arrow">&rarr;</span>' +
      '</a></li>';
  }

  function renderProductResult(product) {
    if (!product) return '';
    var image = product.image || (product.featured_image && product.featured_image.url) || '';
    var title = product.title || '';
    var price = '';
    if (product.price) {
      price = '$' + (parseFloat(product.price) / 100).toFixed(2);
    } else if (product.price_min) {
      price = 'From $' + (parseFloat(product.price_min) / 100).toFixed(2);
    }
    var url = product.url || '#';

    return '<li class="cro-search-product-item">' +
      '<a href="' + escapeHtml(url) + '" class="cro-search-product-link">' +
      (image ? '<img class="cro-search-product-thumb" src="' + escapeHtml(getSizedImage(image, '80x80')) + '" alt="' + escapeHtml(title) + '" loading="lazy" width="40" height="40" />' : '<span class="cro-search-product-thumb-placeholder"></span>') +
      '<span class="cro-search-product-text">' +
      '<span class="cro-search-product-title">' + escapeHtml(title) + '</span>' +
      (price ? '<span class="cro-search-product-price">' + escapeHtml(price) + '</span>' : '') +
      '</span>' +
      '<span class="cro-search-result-arrow">&rarr;</span>' +
      '</a></li>';
  }

  /* -----------------------------------------------------------------------
     Keyboard navigation
     ----------------------------------------------------------------------- */
  input.addEventListener('keydown', function (e) {
    if (allResultLinks.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusedIndex = Math.min(focusedIndex + 1, allResultLinks.length - 1);
      updateFocus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusedIndex = Math.max(focusedIndex - 1, -1);
      updateFocus();
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      var link = allResultLinks[focusedIndex];
      if (link && link.href) {
        closeSearch();
        window.location.href = link.href;
      }
    }
  });

  function updateFocus() {
    allResultLinks.forEach(function (el, i) {
      if (i === focusedIndex) {
        el.classList.add('cro-search--focused');
        el.scrollIntoView({ block: 'nearest' });
      } else {
        el.classList.remove('cro-search--focused');
      }
    });

    /* Update products to match focused location */
    if (focusedIndex >= 0) {
      var focusedLink = allResultLinks[focusedIndex];
      var locItem = focusedLink ? focusedLink.closest('.cro-search-result-item') : null;
      if (locItem) {
        /* Find which location index this item corresponds to */
        var locItems = Array.prototype.slice.call(
          resultsEl.querySelectorAll('.cro-search-result-item')
        );
        var locIdx = locItems.indexOf(locItem);
        if (locIdx >= 0 && currentLocationResults[locIdx]) {
          clearTimeout(hoverTimer);
          setActiveLocation(locItem, currentLocationResults[locIdx]);
        }
      } else {
        /* Focused on a product link — clear active location highlight but keep products */
        if (activeLocItem) {
          activeLocItem.classList.remove('cro-search-result-item--active');
          activeLocItem = null;
        }
      }
    } else {
      /* Focus returned to input (-1) — restore defaults */
      clearTimeout(hoverTimer);
      clearActiveLocation();
    }
  }

  /* -----------------------------------------------------------------------
     Helpers
     ----------------------------------------------------------------------- */
  function highlightMatch(text, query) {
    if (!query) return escapeHtml(text);
    var idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return escapeHtml(text);
    return escapeHtml(text.substring(0, idx)) +
      '<mark>' + escapeHtml(text.substring(idx, idx + query.length)) + '</mark>' +
      escapeHtml(text.substring(idx + query.length));
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function handleize(str) {
    /* Match Shopify's handle filter: lowercase, replace non-alphanumeric with hyphens, collapse, trim */
    return str.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  function getSizedImage(url, size) {
    /* Shopify image URL sizing: insert _SIZEx before extension */
    if (!url || typeof url !== 'string') return '';
    /* Avoid double-sizing if already sized */
    if (url.indexOf('_' + size) > -1) return url;
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/, '_' + size + '.$1$2');
  }

})();
